// FLAGS: none
// Placeholder event detail screen — echoes the :id from the URL into a title and fetches nothing.
import { useState } from "react";
import { ArrowLeft, BarChart3, Mail, Pencil, Ban } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { Link, useParams } from "react-router";
import { toast } from "sonner";
import { Button } from "@openup/shared/primitives/ui/button";
import Spinner from "@openup/shared/components/Spinner/Spinner";
import { Badge } from "@openup/shared/primitives/ui/badge";
import { SpaceSession } from "@src/models/spaces";
import { SpacesApiError } from "@src/services/spacesError";
import CancelDialog from "@src/components/spaces/CancelDialog";
import SessionsTable from "@src/components/spaces/SessionsTable";
import { isEventCancelled } from "@src/components/spaces/editEventForm";
import {
  useCancelEventMutation,
  useCancelSessionMutation,
  useEvent,
} from "@src/queries/events";

/** What the open cancel dialog refers to. */
type CancelTarget =
  | { kind: "event" }
  | { kind: "session"; session: SpaceSession };

const EventDetailPage = () => {
  const { id = "" } = useParams();
  const { data: event, isLoading, isError } = useEvent(id);
  const cancelEventMutation = useCancelEventMutation(id);
  const cancelSessionMutation = useCancelSessionMutation(id);
  const [cancelTarget, setCancelTarget] = useState<CancelTarget | null>(null);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (isError || !event) {
    return (
      <div className="mx-auto max-w-3xl">
        <p className="text-red-600">Event not found.</p>
        <Link
          to="/"
          className="mt-3 inline-flex items-center gap-1 text-green-700"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Back to events
        </Link>
      </div>
    );
  }

  const hasCancellableSessions = event.sessions.some(
    (session) => session.status === "scheduled",
  );
  const cancelled = isEventCancelled(event);

  const onConfirmCancel = async (reason: string | null) => {
    if (!cancelTarget) return;
    try {
      if (cancelTarget.kind === "event") {
        const result = await cancelEventMutation.mutateAsync(reason);
        toast.success(
          `${result.cancelledSessionIds.length} session(s) cancelled. Registered attendees have been emailed.`,
        );
      } else {
        await cancelSessionMutation.mutateAsync({
          sessionId: cancelTarget.session.id,
          reason,
        });
        toast.success(
          "Session cancelled. Registered attendees have been emailed.",
        );
      }
      setCancelTarget(null);
    } catch (error) {
      toast.error(
        error instanceof SpacesApiError
          ? error.detail.message
          : "Cancelling failed. Please try again.",
      );
    }
  };

  return (
    <div className="mx-auto max-w-6xl">
      <Link
        to="/"
        className="mb-4 inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Back to events
      </Link>

      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="mb-1 flex items-center gap-2">
            <h1 className="text-2xl font-semibold text-indigo-800">
              {event.title}
            </h1>
            <Badge variant="light">Private</Badge>
          </div>
          <p className="text-gray-600">{event.employerName}</p>
          {event.description && (
            <div className="mt-2 max-w-2xl text-sm text-gray-700 [&_a]:text-green-700 [&_a]:underline [&_h1]:font-semibold [&_h2]:font-semibold [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-2 [&_strong]:font-semibold [&_ul]:list-disc [&_ul]:pl-5">
              <ReactMarkdown>{event.description}</ReactMarkdown>
            </div>
          )}
        </div>
        <div className="flex shrink-0 gap-2">
          {/* Cancelled events are read-only — no edit entry point. */}
          {!cancelled && (
            <Button asChild variant="secondary" size="sm">
              <Link to={`/events/${event.id}/edit`}>
                <Pencil className="mr-1 h-4 w-4" aria-hidden />
                Edit
              </Link>
            </Button>
          )}
          <Button asChild variant="secondary" size="sm">
            <Link to={`/events/${event.id}/invitees`}>
              <Mail className="mr-1 h-4 w-4" aria-hidden />
              Invitees
            </Link>
          </Button>
          <Button asChild variant="secondary" size="sm">
            <Link to={`/events/${event.id}/attendance`}>
              <BarChart3 className="mr-1 h-4 w-4" aria-hidden />
              Attendance
            </Link>
          </Button>
          {hasCancellableSessions && (
            <Button
              variant="destructive"
              size="sm"
              // The shared `destructive` token resolves to invalid CSS under this
              // app's Tailwind v3 preset — pin the palette explicitly.
              className="bg-red-600 text-white hover:bg-red-700"
              onClick={() => setCancelTarget({ kind: "event" })}
            >
              <Ban className="mr-1 h-4 w-4" aria-hidden />
              Cancel event
            </Button>
          )}
        </div>
      </div>

      <h2 className="mb-2 text-lg font-semibold text-indigo-800">
        Sessions ({event.sessions.length})
      </h2>
      <SessionsTable
        sessions={event.sessions}
        onCancelSession={(session) =>
          setCancelTarget({ kind: "session", session })
        }
      />

      <CancelDialog
        open={cancelTarget !== null}
        title={
          cancelTarget?.kind === "session" ? "Cancel session" : "Cancel event"
        }
        description={
          cancelTarget?.kind === "session"
            ? `"${cancelTarget.session.title}" will be cancelled and its registered attendees will be emailed. This cannot be undone.`
            : "All upcoming sessions of this event will be cancelled and their registered attendees will be emailed. This cannot be undone."
        }
        pending={
          cancelEventMutation.isPending || cancelSessionMutation.isPending
        }
        onConfirm={onConfirmCancel}
        onClose={() => setCancelTarget(null)}
      />
    </div>
  );
};

export default EventDetailPage;
